/**
 * ConventionModeBanner
 *
 * MAESTRO:CONACCESS P3 — per MDM-CONACCESS-001 Section 10.
 * Homepage banner that auto-renders when Convention Mode is active. Polls
 * /api/convention-access/banner-state every 60s.
 */

import { useEffect, useState, useRef } from 'react';
import { getBannerState } from '../services/conventionAccessApi';

const POLL_INTERVAL_MS = 60_000;

export default function ConventionModeBanner() {
  const [state, setState] = useState({ active: false });
  const [dismissed, setDismissed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let timer = null;

    const poll = async () => {
      try {
        const next = await getBannerState();
        if (mountedRef.current) {
          setState(next || { active: false });
        }
      } catch {
        if (mountedRef.current) {
          setState({ active: false });
        }
      }
    };

    poll();
    timer = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      if (timer) {
        clearInterval(timer);
      }
    };
  }, []);

  if (!state?.active || dismissed) {
    return null;
  }

  const venueLabel = state.convention_name || state.venue_name;
  const addressLabel = state.venue_address;

  return (
    <div
      data-testid="convention-mode-banner"
      role="region"
      aria-label="Convention mode banner"
      className="w-full bg-orange-500 text-zinc-950 px-4 py-3 flex items-center justify-between gap-3 shadow-lg"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span aria-hidden="true" className="text-xl">
          🎟️
        </span>
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 text-sm sm:text-base">
          <span className="font-semibold">
            We&apos;re live at {venueLabel}
            {state.ends_at ? ' — scan our QR at the booth for free grading!' : '!'}
          </span>
          {addressLabel ? (
            <span className="text-zinc-900/80 text-xs sm:text-sm">{addressLabel}</span>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href="/conventions"
          className="hidden sm:inline-block text-xs sm:text-sm font-semibold underline decoration-2 underline-offset-2 hover:text-zinc-800"
        >
          View Convention Schedule
        </a>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss convention banner"
          className="px-2 py-1 rounded bg-zinc-950/10 hover:bg-zinc-950/20 text-xs font-semibold"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
